/* ==========================================================
   LUXURY HEADER JS
   PART 5
   ========================================================== */

document.addEventListener('DOMContentLoaded', () => {

  /* ==========================================================
     STICKY HEADER
     ========================================================== */

  const header = document.querySelector('.luxury-header');

  if (header) {

    /* Above 990px base.css leaves html and body at overflow:hidden and makes .page-wrapper the
       scroll container, so window.scrollY never moves and a scroll event on .page-wrapper does
       not bubble up to window. Below that width the document scrolls in the usual way. Read both
       and take whichever is actually moving, rather than duplicating the breakpoint here. */
    const scrollRoot = document.querySelector('.page-wrapper');

    const handleHeaderScroll = () => {

      const scrollTop = Math.max(window.scrollY, scrollRoot?.scrollTop ?? 0);

      header.classList.toggle('scrolled', scrollTop > 40);

    };

    handleHeaderScroll();

    window.addEventListener('scroll', handleHeaderScroll, {
      passive: true
    });

    scrollRoot?.addEventListener('scroll', handleHeaderScroll, {
      passive: true
    });

  }

  /* ==========================================================
     MOBILE DRAWER
     ========================================================== */

  const drawer = document.querySelector('.luxury-mobile-menu');
  const overlay = document.querySelector('.luxury-mobile-overlay');

  const openBtn =
    document.querySelector('.luxury-hamburger');

  const closeBtn =
    document.querySelector('.luxury-mobile-close');

  const openDrawer = () => {

    if (!drawer) return;

    drawer.classList.add('active');

    overlay?.classList.add('active');

    document.body.classList.add('overflow-hidden');

  };

  const closeDrawer = () => {

    drawer?.classList.remove('active');

    overlay?.classList.remove('active');

    document.body.classList.remove('overflow-hidden');

  };

  openBtn?.addEventListener('click', openDrawer);

  closeBtn?.addEventListener('click', closeDrawer);

  overlay?.addEventListener('click', closeDrawer);

  /* ==========================================================
     ESCAPE KEY SUPPORT
     ========================================================== */

  document.addEventListener('keydown', (event) => {

    if (event.key === 'Escape') {
      closeDrawer();
    }

  });

  /* ==========================================================
     FOCUS TRAP
     ========================================================== */

  if (drawer) {

    drawer.addEventListener('keydown', (event) => {

      if (event.key !== 'Tab') return;

      const focusable = drawer.querySelectorAll(
        'a, button, input, select, textarea'
      );

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (
        event.shiftKey &&
        document.activeElement === first
      ) {

        event.preventDefault();
        last.focus();

      }

      if (
        !event.shiftKey &&
        document.activeElement === last
      ) {

        event.preventDefault();
        first.focus();

      }

    });

  }

  /* ==========================================================
     BUTTON RIPPLE EFFECT
     ========================================================== */

  document
    .querySelectorAll(
      '.luxury-action-btn'
    )
    .forEach((button) => {

      button.addEventListener('click', (event) => {

        const ripple =
          document.createElement('span');

        ripple.className = 'luxury-ripple';

        const rect =
          button.getBoundingClientRect();

        ripple.style.left =
          event.clientX - rect.left + 'px';

        ripple.style.top =
          event.clientY - rect.top + 'px';

        button.appendChild(ripple);

        setTimeout(() => {
          ripple.remove();
        }, 600);

      });

    });

  /* ==========================================================
     RESIZE HANDLER
     ========================================================== */

  let resizeTimer;

  window.addEventListener('resize', () => {

    clearTimeout(resizeTimer);

    resizeTimer = setTimeout(() => {

      if (window.innerWidth > 989) {
        closeDrawer();
      }

    }, 200);

  });

});

/* ==========================================================
   CART COUNT
   ========================================================== */

/*
  A drawer cart changes the cart without a page load, so the server-rendered badge would go
  stale where a link to /cart used to refresh it. The standard header's <cart-icon> listens
  to this same Shopify standard event for its own bubble; this keeps the luxury badge in step
  rather than reimplementing the cart logic. Bound on document, so it needs no DOM to exist
  yet and survives section re-renders in the theme editor.
*/
document.addEventListener('shopify:cart:lines-update', (event) => {
  event.promise
    ?.then(({ cart, detail }) => {
      const count = cart?.totalQuantity ?? detail?.itemCount ?? 0;

      for (const badge of document.querySelectorAll('[data-luxury-cart-count]')) {
        badge.textContent = count;
      }
    })
    .catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[luxury-header] cart count not updated:', error);
    });
});

/* ==========================================================
   MEGA MENU PANELS
   ========================================================== */

/*
  One controller for every panel, keyed off each trigger's own aria-controls. The previous
  implementation used document.querySelector, singular, so only ever one panel worked.

  A trigger is server-rendered as an anchor carrying its real destination, so the menu item
  still navigates with JavaScript failed. Here it is upgraded to a disclosure: the click is
  prevented and toggles the panel instead.

  Hover opens for a mouse only, discriminated on the event's own pointerType rather than a
  (hover: hover) media query — a touchscreen laptop reports hover, so the query bound hover
  handlers that made a single tap open then immediately close the panel.

  Keyboard focus never opens a panel on its own, which dropdown-navigation-accessibility.mdc
  requires. Explicit activation does move focus into the panel; see the click handler.
*/
(() => {
  const CLOSE_DELAY = 150;
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /** @type {{trigger: HTMLElement | null, panel: HTMLElement} | null} */
  let open = null;
  let closeTimer;

  const close = () => {
    if (!open) return;
    open.panel.hidden = true;
    // A panel revealed in the theme editor may have no trigger to keep in step.
    open.trigger?.setAttribute('aria-expanded', 'false');
    open = null;
  };

  const show = (trigger, panel) => {
    clearTimeout(closeTimer);
    if (open?.panel === panel) return;
    close();
    panel.hidden = false;
    trigger?.setAttribute('aria-expanded', 'true');
    open = { trigger: trigger ?? null, panel };
  };

  const scheduleClose = () => {
    clearTimeout(closeTimer);
    closeTimer = setTimeout(() => {
      // A leave notification can arrive while the pointer is still over a label or its
      // chevron. Check the current hover region before dismissing the open panel.
      const hoverTarget = open?.trigger?.closest('.luxury-nav-item') ?? open?.trigger;
      if (hoverTarget?.matches(':hover') || open?.panel.matches(':hover')) return;
      close();
    }, CLOSE_DELAY);
  };

  /*
    The panel cannot sit next to its trigger in the DOM: only one blocks content_for tag is
    permitted per file, so every panel renders in one container after the nav. The next
    tabbable element after a trigger is therefore the next menu item, not a card, and no key
    sequence would reach the panel's contents on its own. Explicit activation moves focus
    into the panel instead, which is the standard remedy for a disclosure whose content is
    not adjacent to its launcher. Opening on focus alone is still forbidden, and still never
    happens.
  */
  const focusInto = (panel) => {
    const first = panel.querySelector(FOCUSABLE);
    if (first instanceof HTMLElement) first.focus();
  };

  /*
    Pair each panel with the menu item it targets, then upgrade that anchor into a disclosure.

    The matching cannot happen in Liquid. A section can enumerate its theme blocks but cannot
    read their settings: section.blocks reports every block.type as the literal '@theme' and
    every block.settings.* as nil, measured on a live theme. So each panel publishes its own
    data-trigger-url and data-trigger-title, the nav renders plain links, and the pairing
    happens here.

    URL first, because a URL survives translation; the title is only a fallback, and it is
    matched case-insensitively on trimmed text because it is the merchant retyping a menu
    label by hand.
  */
  const normalisePath = (value) => {
    if (!value) return '';
    // A url setting can arrive as a shopify:// resource URI rather than a resolved path.
    const path = value.replace(/^shopify:\/\//, '/');
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const withoutRoot = root !== '/' && path.startsWith(root) ? path.slice(root.length) : path;
    // Trailing slashes and casing are not meaningful in a handle comparison.
    return ('/' + withoutRoot.replace(/^\/+/, '').replace(/\/+$/, '')).toLowerCase();
  };

  const findTriggerFor = (panel) => {
    const links = [...document.querySelectorAll('.luxury-nav-link[data-menu-url]')];
    const wantedPath = normalisePath(panel.dataset.triggerUrl);

    if (wantedPath !== '/') {
      const byUrl = links.find((a) => normalisePath(a.dataset.menuUrl) === wantedPath);
      if (byUrl) return byUrl;
    }

    const wantedTitle = (panel.dataset.triggerTitle || '').trim().toLowerCase();
    if (!wantedTitle) return null;

    return links.find((a) => a.textContent.trim().toLowerCase() === wantedTitle) ?? null;
  };

  const bind = (panel) => {
    if (panel.dataset.luxuryMegaBound === 'true') return;

    const trigger = findTriggerFor(panel);

    // A panel with no trigger yet is not an error: a merchant adds the panel before choosing
    // which item opens it. The theme editor still reveals it on selection.
    if (!trigger) return;

    panel.dataset.luxuryMegaBound = 'true';
    upgrade(trigger, panel);
  };

  /*
    A dropdown built from the menu's own second and third levels, for the items no panel claims.
    It needs no pairing: the section renders it as a sibling of its own anchor, so the trigger is
    simply the anchor beside it.
  */
  const bindDropdown = (panel) => {
    if (panel.dataset.luxuryDropdownBound === 'true') return;

    const trigger = panel.parentElement?.querySelector('.luxury-nav-link');

    if (!trigger) return;

    /*
      aria-controls on the anchor means a mega menu panel got there first, and the panel is the
      richer of the two. Nothing can reach this dropdown without a trigger, so remove it rather
      than leave a hidden second copy of the same links in the document.
    */
    if (trigger.hasAttribute('aria-controls')) {
      panel.remove();
      return;
    }

    panel.dataset.luxuryDropdownBound = 'true';
    upgrade(trigger, panel);
  };

  /*
    Turn an anchor into a disclosure over a panel. Shared by both kinds so that a dropdown and a
    mega menu behave identically: same hover, same click, same Escape, same focus handling.
  */
  const upgrade = (trigger, panel) => {
    trigger.classList.add('luxury-nav-link--trigger');
    trigger.setAttribute('aria-controls', panel.id);
    trigger.setAttribute('aria-expanded', 'false');

    // Enter on the anchor produces click, so this covers the keyboard too. The default
    // navigation is the no-JavaScript fallback and is not wanted once this handler exists.
    trigger.addEventListener('click', (event) => {
      // Let a modified click through: the element genuinely advertises an href.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

      event.preventDefault();

      if (open?.panel === panel) {
        close();
        return;
      }

      show(trigger, panel);
      focusInto(panel);
    });

    // The entire menu item is one hover region, including the label and chevron.
    const hoverTarget = trigger.closest('.luxury-nav-item') ?? trigger;
    const handleHover = (event) => {
      if (event.pointerType === 'mouse' && event.buttons === 0) show(trigger, panel);
    };

    hoverTarget.addEventListener('pointerenter', handleHover);
    hoverTarget.addEventListener('pointermove', handleHover);
    hoverTarget.addEventListener('pointerleave', (event) => {
      if (event.pointerType !== 'mouse') return;
      if (event.relatedTarget instanceof Node && hoverTarget.contains(event.relatedTarget)) return;
      scheduleClose();
    });

    panel.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'mouse') clearTimeout(closeTimer);
    });

    panel.addEventListener('pointerleave', (event) => {
      if (event.pointerType === 'mouse') scheduleClose();
    });
  };

  const init = () => {
    // A section re-render detaches whatever was open, so the reference is stale, not open.
    if (open && !open.panel.isConnected) open = null;

    // Panels first, always. A dropdown stands down for whichever item a panel has claimed, and
    // that is only knowable once the pairing has run.
    for (const panel of document.querySelectorAll('.luxury-mega-panel')) {
      bind(panel);
    }

    for (const panel of document.querySelectorAll('.luxury-dropdown')) {
      bindDropdown(panel);
    }
  };

  init();

  /*
    The theme editor replaces the section's markup on every setting change, so the triggers
    bound above are detached. Re-bind rather than relying on the deferred script tag inside
    the section body re-executing, which it does not on the Section Rendering API path. The
    document-level listeners below are registered once, outside init, so they are never
    doubled up and they exist even when the section has no panels at all — which is the state
    every merchant starts from.
  */
  document.addEventListener('shopify:section:load', init);

  // Escape closes from anywhere, including from inside the panel, and hands focus back.
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !open) return;
    const { trigger } = open;
    close();
    trigger?.focus();
  });

  // A click outside closes, so a panel opened by tap can be dismissed.
  document.addEventListener('click', (event) => {
    if (!open) return;
    if (open.trigger?.contains(event.target) || open.panel.contains(event.target)) return;
    close();
  });

  /*
    Tabbing right out of the header closes. The boundary is the header region, not the
    trigger-or-panel pair: the panels container is a sibling of .luxury-header-inner, so
    focus moving from a trigger into the panel — or across the remaining nav items and the
    action buttons on the way there — is movement within the header and must not close
    anything. relatedTarget is null when focus leaves the document entirely, which is not a
    reason to close either.
  */
  document.addEventListener('focusout', (event) => {
    if (!open) return;
    const next = event.relatedTarget;
    if (!next) return;
    const region = open.panel.closest('.luxury-header');
    if (!region || region.contains(next)) return;
    close();
  });

  /*
    Theme editor support. A panel starts hidden, so selecting its block would otherwise show
    the merchant nothing at all. A newly added panel has no trigger_url yet and so matches no
    menu item: reveal it anyway, or the merchant's first panel is invisible with no error.
  */
  document.addEventListener('shopify:block:select', (event) => {
    const panel = event.target.closest?.('.luxury-mega-panel');
    if (!panel) return;
    show(document.querySelector(`[aria-controls="${panel.id}"]`), panel);
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    if (event.target.closest?.('.luxury-mega-panel')) close();
  });
})();


(() => {
  const initCollectionNavigation = () => {
    document.querySelectorAll('[data-sa-shop-menu]').forEach(menu => {
      if (menu.dataset.saShopBound === 'true') return;
      menu.dataset.saShopBound = 'true';
      const tabs = [...menu.querySelectorAll('[data-sa-shop-tab]')];
      const panels = [...menu.querySelectorAll('[data-sa-shop-panel]')];
      tabs.forEach(tab => tab.addEventListener('click', () => {
        tabs.forEach(button => button.setAttribute('aria-pressed', String(button === tab)));
        panels.forEach(panel => { panel.hidden = panel.dataset.saShopPanel !== tab.dataset.saShopTab; });
      }));
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initCollectionNavigation);
  else initCollectionNavigation();
  document.addEventListener('shopify:section:load', initCollectionNavigation);
})();

